'use client';

import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import React, { useState, useEffect, useCallback } from 'react';
import { PulseIconButton } from 'src/components/PulseUI';

interface Toast {
  id: number;
  category: string;
  severity: string;
  title: string;
  body: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  territory_optimization: 'Territory',
  export: 'Export',
  strategy: 'Strategy',
  crm_sync: 'CRM Sync',
  encounters: 'Encounters',
};

const SEVERITY_STYLES: Record<string, string> = {
  success: 'bg-green-50 border-green-400 text-green-800',
  info: 'bg-blue-50 border-blue-400 text-blue-800',
  warning: 'bg-yellow-50 border-yellow-400 text-yellow-800',
  error: 'bg-red-50 border-red-400 text-red-800',
};

const AUTO_DISMISS_MS = 5000;

// --- Imperative API ---

type ToastListener = (toast: Toast) => void;
let listener: ToastListener | null = null;

export function showWebSocketToast(toast: Toast): void {
  listener?.(toast);
}

const WebSocketsToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Toast) => {
    setToasts((prevToasts) => [...prevToasts, toast]);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  // Register the listener on mount and clean up on unmount
  useEffect(() => {
    listener = addToast;
    return () => {
      listener = null;
    };
  }, [addToast]);

  // Auto-dismiss toasts after a delay
  useEffect(() => {
    if (toasts.length === 0) return;

    const oldest = toasts[0];
    const timer = setTimeout(() => {
      removeToast(oldest.id);
    }, AUTO_DISMISS_MS);

    return () => clearTimeout(timer);
  }, [toasts, removeToast]);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      className="fixed bottom-4 right-4 space-y-2 z-50 flex flex-col gap-2"
      data-testid="websocket-toast-container"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg 
  transition-all animate-in slide-in-from-right ${
    SEVERITY_STYLES[toast.severity] || SEVERITY_STYLES.info
  }`}
          role="alert"
          aria-live="polite"
          data-testid="websocket-toast"
        >
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide opacity-60">
              {CATEGORY_LABELS[toast.category] || toast.category}
            </p>
            <p className="font-medium">{toast.title}</p>
            {toast.body && <p className="text-sm opacity-75">{toast.body}</p>}
          </div>
          <PulseIconButton
            variant="plain"
            size="sm"
            icon={faXmark}
            onClick={() => removeToast(toast.id)}
            className="text-current opacity-40 hover:opacity-100"
            aria-label="Dismiss notification"
            data-testid="websocket-toast-dismiss"
          />
        </div>
      ))}
    </div>
  );
};

export default WebSocketsToast;
